import { Menu, Bell } from 'lucide-react';
import { useLingui } from '@lingui/react';
import { useLocation } from 'react-router-dom';
import { locales, dynamicActivate, type Locale } from '../../i18n';
import * as Flags from 'country-flag-icons/react/3x2';

/** Dil kodu → ISO 3166-1 alpha-2 ülke kodu */
const LOCALE_TO_COUNTRY: Record<string, string> = {
  tr: 'TR',
  en: 'GB',
  pl: 'PL',
};

function LocaleFlag({ locale, className = 'w-6 h-4' }: { locale: string; className?: string }) {
  const countryCode = LOCALE_TO_COUNTRY[locale];
  if (!countryCode) return null;
  const FlagComponent = (
    Flags as Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>
  )[countryCode];
  if (!FlagComponent) return null;
  return <FlagComponent className={className} aria-hidden="true" />;
}

interface TopbarProps {
  /** drawer input id'si ile eşleşmeli */
  drawerId?: string;
}

function LanguageSwitcher() {
  const { i18n: lingui, _ } = useLingui();

  async function handleChange(locale: Locale) {
    localStorage.setItem('locale', locale);
    await dynamicActivate(locale);
  }

  return (
    <div className="dropdown dropdown-end">
      <button
        tabIndex={0}
        className="btn btn-ghost btn-circle text-base-content"
        aria-label={_({ id: 'nav.language.selectLabel' })}
        title={locales[lingui.locale] ?? lingui.locale}
      >
        <LocaleFlag locale={lingui.locale} className="w-6 h-4 rounded-sm shadow-sm shrink-0" />
      </button>
      <ul
        tabIndex={0}
        className="dropdown-content menu bg-base-100 rounded-box z-50 w-36 p-1 shadow border border-base-300 mt-1"
      >
        {Object.entries(locales).map(([locale, label]) => (
          <li key={locale}>
            <button
              className={`text-sm flex items-center gap-2 ${lingui.locale === locale ? 'active font-semibold' : ''}`}
              onClick={() => {
                void handleChange(locale);
                (document.activeElement as HTMLElement)?.blur();
              }}
            >
              <LocaleFlag locale={locale} className="w-5 h-3.5 rounded-sm shadow-sm shrink-0" />
              {label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Topbar({ drawerId = 'sidebar-drawer' }: TopbarProps) {
  const { pathname } = useLocation();
  const { _ } = useLingui();

  const PAGE_TITLES: Record<string, string> = {
    '/dashboard': _({ id: 'nav.pageTitle.dashboard' }),
    '/portfolio': _({ id: 'nav.pageTitle.portfolio' }),
    '/exchanges': _({ id: 'nav.pageTitle.exchanges' }),
    '/exchanges/new': _({ id: 'nav.pageTitle.exchangesNew' }),
    '/import': _({ id: 'nav.pageTitle.import' }),
    '/profile': _({ id: 'nav.pageTitle.profile' }),
    '/admin': _({ id: 'nav.pageTitle.admin' }),
    '/admin/exchanges': _({ id: 'nav.pageTitle.adminExchanges' }),
  };
  const pageTitle = PAGE_TITLES[pathname] ?? '';

  return (
    <nav className="navbar bg-base-100 border-b border-base-300 sticky top-0 z-30 shadow-sm min-h-14 px-2 sm:px-3 md:px-4">
      <div className="navbar-start gap-1">
        {/* Hamburger — yalnızca lg altında görünür */}
        <label
          htmlFor={drawerId}
          className="btn btn-ghost btn-circle lg:hidden!"
          aria-label={_({ id: 'nav.sidebar.openLabel' })}
        >
          <Menu
            size={20}
            strokeWidth={2.25}
            className="shrink-0 text-base-content"
            aria-hidden="true"
          />
        </label>
        <span className="text-sm font-semibold text-base-content lg:hidden">{pageTitle}</span>
      </div>

      <div className="navbar-end gap-1">
        <LanguageSwitcher />
        <div className="tooltip tooltip-bottom" data-tip={_({ id: 'nav.notifications.tooltip' })}>
          <button
            className="btn btn-ghost btn-circle relative opacity-60 text-base-content cursor-not-allowed"
            aria-label={_({ id: 'nav.notifications.ariaLabel' })}
            disabled
          >
            <Bell
              size={18}
              strokeWidth={2.25}
              className="shrink-0 text-base-content"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </nav>
  );
}
